/**
 * Hook para gerenciar Stress Test de Campanhas
 * Reutiliza infraestrutura existente (campaign pipeline + stress-test provider)
 * Sem duplicação de Edge Functions
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger com namespace fixo para o módulo
const log = createLogger('CampaignStressTest');

// Interfaces para o stress test de campanhas
export interface CampaignStressTestConfig {
  tenantId: string;
  inboxIds: string[];  // Múltiplas inboxes para balanceamento
  contactCount: number;
  messageContent: string;
  delayMinMs: number;
  delayMaxMs: number;
  blockSize: number;
  blockDelayMinMs: number;
  blockDelayMaxMs: number;
  // Grupo de Blocos
  groupBlockEnabled?: boolean;
  groupBlockIntervalMinMinutes?: number;
  groupBlockIntervalMaxMinutes?: number;
  groupBlockSize?: number;
  // Opções extras
  useContactName?: boolean;
  verifyWhatsapp?: boolean;
}

export interface CampaignStressTestStats {
  totalRecipients: number;
  pendingCount: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  progressPercent: number;
  throughputPerSecond: number;
  avgLatencyMs: number;
}

/**
 * Hook para buscar inboxes com provider stress-test
 */
export function useStressTestInboxes(tenantId: string | null) {
  return useQuery({
    queryKey: ['stress-test-inboxes', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('inboxes')
        .select(`
          id,
          name,
          identifier,
          api_providers!inner (
            slug,
            display_name
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('api_providers.slug', 'stress-test')
        .eq('status', 'active');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });
}

/**
 * Hook para buscar campanhas de teste ativas
 */
export function useStressTestCampaigns(tenantId: string | null) {
  return useQuery({
    queryKey: ['stress-test-campaigns', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          status,
          total_recipients,
          sent_count,
          delivered_count,
          failed_count,
          started_at,
          completed_at,
          created_at
        `)
        .eq('tenant_id', tenantId)
        .ilike('name', '%[STRESS-TEST]%')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
    refetchInterval: 3000, // Atualiza a cada 3 segundos durante teste
  });
}

/**
 * Hook para estatísticas em tempo real de uma campanha
 */
export function useCampaignStressTestStats(campaignId: string | null) {
  return useQuery({
    queryKey: ['campaign-stress-stats', campaignId],
    queryFn: async (): Promise<CampaignStressTestStats | null> => {
      if (!campaignId) return null;
      
      // Buscar campanha
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('total_recipients, sent_count, delivered_count, failed_count, started_at')
        .eq('id', campaignId)
        .single();
      
      if (campaignError) throw campaignError;
      
      // Buscar contagem de recipients por status
      const { data: recipients, error: recipientsError } = await supabase
        .from('campaign_recipients')
        .select('status')
        .eq('campaign_id', campaignId);
      
      if (recipientsError) throw recipientsError;
      
      const totalRecipients = campaign.total_recipients || 0;
      const sentCount = campaign.sent_count || 0;
      const deliveredCount = campaign.delivered_count || 0;
      const failedCount = campaign.failed_count || 0;
      const pendingCount = totalRecipients - sentCount - failedCount;
      
      // Calcular throughput (mensagens por segundo)
      let throughputPerSecond = 0;
      if (campaign.started_at && sentCount > 0) {
        const elapsedSeconds = (Date.now() - new Date(campaign.started_at).getTime()) / 1000;
        throughputPerSecond = elapsedSeconds > 0 ? sentCount / elapsedSeconds : 0;
      }
      
      // Calcular progresso
      const progressPercent = totalRecipients > 0 
        ? ((sentCount + failedCount) / totalRecipients) * 100 
        : 0;
      
      return {
        totalRecipients,
        pendingCount,
        sentCount,
        deliveredCount,
        failedCount,
        progressPercent,
        throughputPerSecond,
        avgLatencyMs: 0, // TODO: calcular via api_request_logs
      };
    },
    enabled: !!campaignId,
    refetchInterval: 2000, // Atualiza a cada 2 segundos
  });
}

/**
 * Hook principal com ações do stress test
 */
export function useCampaignStressTestActions() {
  const queryClient = useQueryClient();
  
  // Gerar contatos de teste
  const generateContacts = useMutation({
    mutationFn: async ({ tenantId, count }: { tenantId: string; count: number }) => {
      log.info('Gerando contatos de teste', { tenantId, count });
      
      const { data, error } = await supabase.rpc('generate_test_contacts', {
        p_tenant_id: tenantId,
        p_count: count,
        p_prefix: '5500000',
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  
  // Criar campanha de teste
  const createTestCampaign = useMutation({
    mutationFn: async (config: CampaignStressTestConfig) => {
      log.info('Criando campanha de teste', config);
      
      // 1. Criar campanha
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          tenant_id: config.tenantId,
          name: `[STRESS-TEST] ${new Date().toLocaleString('pt-BR')}`,
          message_content: config.messageContent || 'Mensagem de teste de stress #{{numero}}',
          channel_type: 'whatsapp',
          status: 'draft',
          delay_min_ms: config.delayMinMs,
          delay_max_ms: config.delayMaxMs,
          block_size: config.blockSize,
          block_delay_min_ms: config.blockDelayMinMs,
          block_delay_max_ms: config.blockDelayMaxMs,
          // Grupo de Blocos
          group_block_enabled: config.groupBlockEnabled ?? false,
          group_block_interval_min_minutes: config.groupBlockIntervalMinMinutes ?? 10,
          group_block_interval_max_minutes: config.groupBlockIntervalMaxMinutes ?? 20,
          group_block_size: config.groupBlockSize ?? 20,
          // Opções extras
          use_contact_name: config.useContactName ?? true,
          verify_whatsapp: config.verifyWhatsapp ?? true,
        })
        .select()
        .single();
      
      if (campaignError) throw campaignError;
      
      // 2. Vincular múltiplas inboxes com peso distribuído
      if (config.inboxIds.length > 0) {
        const weightPerInbox = Math.floor(100 / config.inboxIds.length);
        const inboxInserts = config.inboxIds.map((inboxId, index) => ({
          campaign_id: campaign.id,
          inbox_id: inboxId,
          // Primeira inbox recebe o resto da divisão para totalizar 100%
          send_weight: index === 0 
            ? 100 - (weightPerInbox * (config.inboxIds.length - 1)) 
            : weightPerInbox,
        }));
        
        const { error: inboxError } = await supabase
          .from('campaign_inboxes')
          .insert(inboxInserts);
        
        if (inboxError) throw inboxError;
      }
      
      // 3. Buscar contatos de teste
      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select('id, phone, name')
        .eq('tenant_id', config.tenantId)
        .eq('is_test', true)
        .limit(config.contactCount);
      
      if (contactsError) throw contactsError;
      
      if (!contacts || contacts.length === 0) {
        throw new Error('Nenhum contato de teste encontrado. Gere contatos primeiro.');
      }
      
      // 4. Criar recipients
      const recipients = contacts.map((contact, index) => ({
        campaign_id: campaign.id,
        contact_id: contact.id,
        phone: contact.phone,
        contact_name: contact.name,
        status: 'pending' as const,
        sort_order: index,
        variables: { numero: index + 1 },
      }));
      
      const { error: recipientsError } = await supabase
        .from('campaign_recipients')
        .insert(recipients);
      
      if (recipientsError) throw recipientsError;
      
      // 5. Atualizar total_recipients
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({ total_recipients: contacts.length })
        .eq('id', campaign.id);
      
      if (updateError) throw updateError;
      
      return { ...campaign, total_recipients: contacts.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stress-test-campaigns'] });
    },
  });
  
  // Iniciar campanha
  const startCampaign = useMutation({
    mutationFn: async (campaignId: string) => {
      log.info('Iniciando campanha', { campaignId });
      
      const { error } = await supabase
        .from('campaigns')
        .update({ 
          status: 'running',
          started_at: new Date().toISOString(),
        })
        .eq('id', campaignId);
      
      if (error) throw error;
      return campaignId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stress-test-campaigns'] });
    },
  });
  
  // Pausar campanha
  const pauseCampaign = useMutation({
    mutationFn: async (campaignId: string) => {
      log.info('Pausando campanha', { campaignId });
      
      const { error } = await supabase
        .from('campaigns')
        .update({ 
          status: 'paused',
          paused_at: new Date().toISOString(),
        })
        .eq('id', campaignId);
      
      if (error) throw error;
      return campaignId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stress-test-campaigns'] });
    },
  });
  
  // Limpar dados de teste
  const cleanupTestData = useMutation({
    mutationFn: async (tenantId: string) => {
      log.info('Limpando dados de teste', { tenantId });
      
      // 1. Deletar campanhas de teste
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id')
        .eq('tenant_id', tenantId)
        .ilike('name', '%[STRESS-TEST]%');
      
      if (campaigns && campaigns.length > 0) {
        const campaignIds = campaigns.map(c => c.id);
        
        // Deletar recipients
        await supabase
          .from('campaign_recipients')
          .delete()
          .in('campaign_id', campaignIds);
        
        // Deletar inboxes vinculadas
        await supabase
          .from('campaign_inboxes')
          .delete()
          .in('campaign_id', campaignIds);
        
        // Deletar logs
        await supabase
          .from('campaign_logs')
          .delete()
          .in('campaign_id', campaignIds);
        
        // Deletar campanhas
        await supabase
          .from('campaigns')
          .delete()
          .in('id', campaignIds);
      }
      
      // 2. Limpar contatos de teste
      const { data: deletedContacts } = await supabase.rpc('cleanup_test_contacts', {
        p_tenant_id: tenantId,
      });
      
      return { 
        campaignsDeleted: campaigns?.length || 0, 
        contactsDeleted: deletedContacts || 0 
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stress-test-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  
  // Contar contatos de teste existentes
  const countTestContacts = async (tenantId: string): Promise<number> => {
    const { count, error } = await supabase
      .from('contacts')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('is_test', true);
    
    if (error) throw error;
    return count || 0;
  };
  
  return {
    generateContacts,
    createTestCampaign,
    startCampaign,
    pauseCampaign,
    cleanupTestData,
    countTestContacts,
  };
}
